/**
 * Pixel Sky — the scene runtime, minus React.
 *
 * Everything here is a pure function of (scene, theme) or a plain DOM style
 * write, so it is unit-testable and cheap: changing scene never re-renders the
 * tree, it rewrites a dozen custom properties on <html> and the CSS follows.
 * `SkyBackdrop.tsx` is the component that calls this on route changes.
 *
 * Which tokens live where (PIXEL-SKY-DESIGN.md §Colour): the card colour,
 * materials, accent and sky image follow the *scene*, so they are written here.
 * The text ramp, the field fill and the opaque raised surfaces follow the
 * *theme* only, so they stay in `index.css` under `[data-sky-theme]`; this file
 * just sets the attribute.
 */

import { SCENES, SCENE_KEYS, SURFACE_ALPHA, type Rgb, type SceneKey, type SkyTheme } from "./sky";

/** `localStorage`: a scene key pins the sky; `"random"` or absent means random. */
export const SKY_STORAGE_KEY = "busee.sky";
/** `localStorage`: `"day" | "night" | "system"`; absent means system. */
export const THEME_STORAGE_KEY = "busee.theme";
/** `sessionStorage`: the scene shown last, so a random pick never repeats it. */
export const LAST_SCENE_STORAGE_KEY = "busee.sky.last";

export type ThemeSetting = SkyTheme | "system";
export type SkySetting = SceneKey | "random";

/** The material rungs, as the card colour's alpha (index.css `--busee-material-*`). */
export const MATERIAL_ALPHA = { thin: 0.6, regular: 0.72, thick: 0.88 } as const;
/** The sunken surface is the card colour darkened to this fraction, at 0.9. */
const SUNKEN = { scale: 0.78, alpha: 0.9 } as const;

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

/** `storage.getItem` that survives a missing or throwing store (private windows, tests). */
function read(storage: Storage | null | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function isSceneKey(value: unknown): value is SceneKey {
  return typeof value === "string" && SCENE_KEYS.some((key) => key === value);
}

export function readSkySetting(storage: Storage | null | undefined): SkySetting {
  const value = read(storage, SKY_STORAGE_KEY);
  return isSceneKey(value) ? value : "random";
}

export function readThemeSetting(storage: Storage | null | undefined): ThemeSetting {
  const value = read(storage, THEME_STORAGE_KEY);
  return value === "day" || value === "night" ? value : "system";
}

export function resolveTheme(setting: ThemeSetting, systemDark: boolean): SkyTheme {
  return setting === "system" ? (systemDark ? "night" : "day") : setting;
}

/**
 * A scene at random that is not `last`. `random` is injectable for tests.
 * With a single scene there is nothing else to pick, so it repeats.
 */
export function pickScene(last: SceneKey | null, random: () => number = Math.random): SceneKey {
  const pool = SCENE_KEYS.filter((key) => key !== last);
  const choices = pool.length > 0 ? pool : SCENE_KEYS;
  return choices[Math.min(choices.length - 1, Math.floor(random() * choices.length))];
}

/** The scene for this route entry: a pinned one if the setting says so, else a fresh random one. */
export function chooseScene(
  setting: SkySetting,
  last: SceneKey | null,
  random: () => number = Math.random,
): SceneKey {
  return setting === "random" ? pickScene(last, random) : setting;
}

export function skyImageUrl(key: SceneKey, theme: SkyTheme): string {
  return `/sky/${key}-${theme}.png`;
}

const rgb = (c: Rgb, alpha?: number): string => {
  const [r, g, b] = c.map((n) => Math.round(n));
  return alpha === undefined ? `rgb(${r} ${g} ${b})` : `rgb(${r} ${g} ${b} / ${alpha})`;
};

/** The custom properties a scene owns, ready to write onto <html>. */
export function sceneTokens(key: SceneKey, theme: SkyTheme): Record<string, string> {
  const scene = SCENES[key];
  const card = scene.surface[theme];
  const sunken: Rgb = [card[0] * SUNKEN.scale, card[1] * SUNKEN.scale, card[2] * SUNKEN.scale];
  return {
    "--busee-bg": rgb(card),
    "--busee-surface": rgb(card, SURFACE_ALPHA),
    "--busee-grouped-surface": rgb(card, SURFACE_ALPHA),
    "--busee-surface-sunken": rgb(sunken, SUNKEN.alpha),
    "--busee-material-thin": rgb(card, MATERIAL_ALPHA.thin),
    "--busee-material-regular": rgb(card, MATERIAL_ALPHA.regular),
    "--busee-material-thick": rgb(card, MATERIAL_ALPHA.thick),
    "--busee-accent": scene.accent,
    "--busee-accent-soft": `color-mix(in srgb, ${scene.accent} 14%, transparent)`,
    "--busee-sky-image": `url(${skyImageUrl(key, theme)})`,
  };
}

/**
 * Writes a scene onto <html>. `sky: false` (Kanban City) keeps the colours but
 * drops the image: the 3D sea there is its own sky.
 */
export function applyScene(
  root: HTMLElement,
  key: SceneKey,
  theme: SkyTheme,
  options: { sky: boolean } = { sky: true },
): void {
  root.dataset.skyTheme = theme;
  root.dataset.skyScene = key;
  for (const [name, value] of Object.entries(sceneTokens(key, theme))) {
    root.style.setProperty(name, value);
  }
  if (!options.sky) {
    root.style.setProperty("--busee-sky-image", "none");
  }
}
